import { useCallback, useEffect, useRef, useState } from 'react';
import { mediaCacheKey } from '../media';

/**
 * Maintains a cache of object URLs for authenticated image fetches.
 * Returns a getter that triggers a fetch on first access and keeps the
 * resulting object URL valid for the lifetime of the component.
 */
export default function useImageCache(
  fetchImage,
  { batch = false, returnState = false } = {},
) {
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const disposedRef = useRef(false);
  const [cacheRevision, setCacheRevision] = useState(0);

  useEffect(() => {
    const cache = cacheRef.current;
    const pending = pendingRef.current;
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      cache.forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
      cache.clear();
      pending.clear();
    };
  }, []);

  const loadImage = useCallback((imageId, variant) => {
    if (!imageId) return null;

    const key = mediaCacheKey(imageId, variant);
    const cache = cacheRef.current;
    if (cache.has(key)) return Promise.resolve(cache.get(key) || null);
    const pending = pendingRef.current.get(key);
    if (pending) return pending;

    const request = fetchImage(imageId, variant)
      .then((url) => {
        if (!url) return null;
        if (disposedRef.current) {
          URL.revokeObjectURL(url);
          return null;
        }
        const existing = cache.get(key);
        if (existing && existing !== url) URL.revokeObjectURL(existing);
        cache.set(key, url);
        if (!batch) setCacheRevision(index => index + 1);
        return url;
      })
      .catch((error) => {
        console.error(`Failed to fetch cached image ${key}`, error);
        return null;
      })
      .finally(() => pendingRef.current.delete(key));
    pendingRef.current.set(key, request);
    return request;
  }, [batch, fetchImage]);

  const getImageUrl = useCallback((imageId, variant) => {
    if (!imageId) return null;
    const key = mediaCacheKey(imageId, variant);
    if (!cacheRef.current.has(key)) {
      loadImage(imageId, variant);
    }
    return cacheRef.current.get(key) || null;
  }, [loadImage]);

  const preload = useCallback(async (images) => {
    const uniqueImages = new Map();
    (images || []).forEach(({ imageId, variant }) => {
      if (imageId) uniqueImages.set(mediaCacheKey(imageId, variant), { imageId, variant });
    });
    await Promise.allSettled([...uniqueImages.values()].map(({ imageId, variant }) => (
      loadImage(imageId, variant)
    )));
    if (!disposedRef.current) {
      setCacheRevision(index => index + 1);
    }
  }, [loadImage]);

  return batch || returnState
    ? { getImageUrl, preload, revision: cacheRevision }
    : getImageUrl;
}
